// MobileApplicationsTab.jsx — mobile-only Applications tab mirroring the
// Flutter applications screen: header + review-count pill, pill-style tab
// chips, and a list of agent-apply cards with pastel status chips.
import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import {
  Trash2, RefreshCw, Clock, Check, AlertCircle, Mail, Lock, HelpCircle,
  Sparkles, CalendarX, Rocket, Hourglass, SearchCheck,
} from "lucide-react"
import {
  collection, deleteDoc, doc, onSnapshot, orderBy, query,
} from "firebase/firestore"
import { db } from "../../firebaseConfig"
import { initials } from "../JobCard"
import MobileApplicationProgressSheet from "./MobileApplicationProgressSheet"
import MobileBottomNav from "./MobileBottomNav"

const FF = '"Plus Jakarta Sans", "Inter", -apple-system, sans-serif'
const APPLICATIONS_API = "https://jobscanner-e1dd4.ue.r.appspot.com/applications"

// Flutter mobile palette
const INK          = "#0F172A"
const INK_MUTED    = "#64748B"
const INK_FAINT    = "#94A3B8"
const BG           = "#FFFFFF"
const SURFACE      = "#FFFFFF"
const SURFACE_ALT  = "#F3F2ED"
const BORDER_SOFT  = "#E6E5DF"
const AGENT_ORANGE = "#E8702A"

const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

// ── Data helpers ───────────────────────────────────────────────────────────
function parseDate(raw) {
  if (!raw) return new Date()
  if (typeof raw?.toDate === "function") return raw.toDate()
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? new Date() : date
}

function ago(date) {
  const diff = Date.now() - date.getTime()
  const mins = Math.max(0, Math.floor(diff / 60000))
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return days === 1 ? "yesterday" : `${days}d ago`
  const months = Math.floor(days / 30)
  return months === 1 ? "1mo ago" : `${months}mo ago`
}

const KNOWN_STATUSES = new Set([
  "pending_optimization", "pending_resume_review", "processing", "completed",
  "failed", "job_expired", "pending_verification", "credentials_error",
  "additional_information_required", "skipped",
])

function parseStatus(status = "") {
  return KNOWN_STATUSES.has(status) ? status : "queued"
}

function normalize(docSnap) {
  const data = docSnap.data() || {}
  return {
    ...data,
    id: docSnap.id,
    jobTitle: data.jobTitle || "",
    company: data.company || "",
    logoUrl: data.logoUrl || "",
    jobUrl: data.jobUrl || "",
    description: data.description || "",
    reviewSections: Array.isArray(data.reviewSections) ? data.reviewSections : [],
    appliedAt: parseDate(data.appliedAt),
    status: parseStatus(data.status),
  }
}

function bucketOf(status) {
  if (["pending_optimization", "pending_resume_review", "additional_information_required",
       "pending_verification", "credentials_error"].includes(status)) return "review"
  if (["queued", "processing"].includes(status)) return "inProgress"
  if (["completed", "job_expired"].includes(status)) return "submitted"
  if (status === "failed") return "failed"
  return "inProgress"
}

const TABS = [
  { id: "review",     label: "Review" },
  { id: "inProgress", label: "In Progress" },
  { id: "submitted",  label: "Submitted" },
  { id: "failed",     label: "Failed", destructive: true },
]

// ── Status chip variants ───────────────────────────────────────────────────
const STATUS_CHIPS = {
  pending_optimization:              { bg: "#FFF1C4", fg: "#854D0E", Icon: Sparkles,     label: "Optimising" },
  pending_resume_review:             { bg: "#DDF3E4", fg: "#166534", Icon: SearchCheck,  label: "Needs Review" },
  additional_information_required:   { bg: "#E6DEFB", fg: "#6D28D9", Icon: HelpCircle,   label: "Info Needed" },
  pending_verification:              { bg: "#FEF3C7", fg: "#854D0E", Icon: Mail,         label: "Verify Email" },
  credentials_error:                 { bg: "#FFE4D6", fg: "#C2410C", Icon: Lock,         label: "Reset Password" },
  queued:                            { bg: SURFACE_ALT, fg: INK_MUTED, Icon: Clock,      label: "Queued" },
  completed:                         { bg: "#DCFCE7", fg: "#15803D", Icon: Check,        label: "Submitted" },
  failed:                            { bg: "#FEE2E2", fg: "#B91C1C", Icon: AlertCircle,  label: "Failed" },
  job_expired:                       { bg: SURFACE_ALT, fg: INK_MUTED, Icon: CalendarX,  label: "Job Expired" },
  skipped:                           { bg: SURFACE_ALT, fg: INK_MUTED, Icon: CalendarX,  label: "Skipped" },
}

function StatusChip({ status, pulse }) {
  if (status === "processing") {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 10px", borderRadius: 999,
        background: `rgba(232,112,42,${0.14 + 0.08 * pulse})`,
        border: `1px solid rgba(232,112,42,${0.35 + 0.2 * pulse})`,
        color: AGENT_ORANGE,
        fontSize: 10, fontWeight: 600,
      }}>
        <span style={{
          width: 5, height: 5, borderRadius: "50%",
          background: `rgba(232,112,42,${0.6 + 0.4 * pulse})`,
        }} />
        Applying
      </span>
    )
  }
  const meta = STATUS_CHIPS[status] || STATUS_CHIPS.queued
  const { bg, fg, Icon, label } = meta
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "5px 10px", borderRadius: 999,
      background: bg, color: fg,
      fontSize: 10, fontWeight: 600,
    }}>
      <Icon size={11} strokeWidth={2.2} />
      {label}
    </span>
  )
}

// ── Company logo ───────────────────────────────────────────────────────────
function CompanyLogo({ company, logoUrl, size = 44 }) {
  const [bfErr, setBfErr] = useState(false)
  const [fbErr, setFbErr] = useState(false)
  const domain = company
    ? company.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null
  const bfUrl = domain ? `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}` : null
  const src = !bfErr ? bfUrl : (!fbErr && logoUrl ? logoUrl : null)
  const onErr = () => { if (!bfErr) setBfErr(true); else setFbErr(true) }

  const base = {
    width: size, height: size, borderRadius: 10, flexShrink: 0,
  }
  if (src) {
    return (
      <span style={{
        ...base, background: "#fff", border: `1px solid ${BORDER_SOFT}`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        <img src={src} alt="" onError={onErr}
          style={{ width: "80%", height: "80%", objectFit: "contain" }} />
      </span>
    )
  }
  return (
    <span style={{
      ...base, background: SURFACE_ALT,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>
      <span style={{ fontSize: size * 0.3, fontWeight: 800, color: INK, letterSpacing: "-0.3px" }}>
        {initials(company)}
      </span>
    </span>
  )
}

// ── Tab chip ───────────────────────────────────────────────────────────────
function TabChip({ label, count, selected, destructive, onClick }) {
  const activeFg = destructive ? "#B91C1C" : INK
  const activeBg = destructive ? "#FDECEC" : SURFACE
  return (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "5px 12px", borderRadius: 20,
      background: selected ? activeBg : SURFACE_ALT,
      border: "none", cursor: "pointer",
      fontFamily: FF,
      whiteSpace: "nowrap", flexShrink: 0,
      boxShadow: selected && !destructive ? "0 1px 6px rgba(15,23,42,0.06)" : "none",
      transition: "background 180ms, color 180ms",
    }}>
      <span style={{
        fontSize: 12, fontWeight: 700,
        color: selected ? activeFg : INK_MUTED,
      }}>{count}</span>
      <span style={{
        fontSize: 12, fontWeight: selected ? 600 : 500,
        color: selected ? activeFg : INK_MUTED,
      }}>{label}</span>
    </button>
  )
}

// ── Application card ───────────────────────────────────────────────────────
function AppCard({ app, onDelete, onTap, onRetry }) {
  const [retrying, setRetrying] = useState(false)
  const [pulse, setPulse] = useState(0)
  const canRetry = ["failed", "pending_verification", "credentials_error"].includes(app.status)

  useEffect(() => {
    if (app.status !== "processing" && app.status !== "pending_optimization") return
    let frame
    const start = performance.now()
    const loop = (t) => {
      const v = 0.5 * (1 + Math.sin(((t - start) / 1600) * Math.PI * 2))
      setPulse(v)
      frame = requestAnimationFrame(loop)
    }
    frame = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(frame)
  }, [app.status])

  const handleRetry = async (e) => {
    e.stopPropagation()
    if (retrying) return
    setRetrying(true)
    try { await onRetry?.(app) }
    finally { setRetrying(false) }
  }

  const isProcessing = app.status === "processing"
  return (
    <button
      onClick={() => onTap?.(app)}
      style={{
        width: "100%", textAlign: "left", background: SURFACE,
        border: `1px solid ${isProcessing ? "rgba(232,112,42,0.35)" : BORDER_SOFT}`,
        borderRadius: 18, padding: 0, fontFamily: FF,
        cursor: "pointer",
        boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
        display: "block",
      }}
    >
      {/* Main */}
      <div style={{ padding: "12px 12px 10px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
        <CompanyLogo company={app.company} logoUrl={app.logoUrl} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: INK, letterSpacing: "-0.2px", lineHeight: 1.25,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>{app.jobTitle || "Untitled role"}</div>
          <div style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
            <span style={{
              fontSize: 12, fontWeight: 500, color: INK_MUTED,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
            }}>{app.company}</span>
            <span style={{ width: 3, height: 3, borderRadius: "50%", background: INK_FAINT, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: INK_FAINT, flexShrink: 0 }}>{ago(app.appliedAt)}</span>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete?.(app.id) }}
          aria-label="Delete"
          style={{
            padding: "0 4px 8px 8px", background: "transparent",
            border: "none", color: INK_FAINT, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Footer */}
      <div style={{ padding: "6px 14px 10px 16px", display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{
          width: 20, height: 20, borderRadius: 6,
          background: "rgba(232,112,42,0.1)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <Rocket size={11} color={AGENT_ORANGE} />
        </span>
        <span style={{ fontSize: 11, fontWeight: 500, color: INK_MUTED }}>Agent Apply</span>
        <span style={{ flex: 1 }} />
        {canRetry && (
          <span
            role="button"
            onClick={handleRetry}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "5px 9px", borderRadius: 20,
              background: "rgba(232,112,42,0.12)",
              border: "1px solid rgba(232,112,42,0.35)",
              color: AGENT_ORANGE,
              fontSize: 10, fontWeight: 600,
              cursor: retrying ? "default" : "pointer",
              opacity: retrying ? 0.6 : 1,
            }}
          >
            <RefreshCw size={11} strokeWidth={2.2} />
            {retrying ? "Retrying…" : "Retry"}
          </span>
        )}
        <StatusChip status={app.status} pulse={pulse} />
      </div>
    </button>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────
function EmptyState({ tab }) {
  if (tab === "submitted") {
    return (
      <EmptyShell
        iconColor="#16A34A"
        Icon={Check}
        title="No completed applications"
        body="Successfully submitted applications will appear here once your agent finishes."
        pillBg="#DCFCE7"
        pillFg="#15803D"
        PillIcon={Hourglass}
        pillLabel="Results from your agent land here"
      />
    )
  }
  if (tab === "failed") {
    return (
      <EmptyShell
        iconColor="#16A34A"
        Icon={Check}
        title="No failed applications"
        body="If an application gets stuck or a role expires, you'll find it here with a one-click retry."
        pillBg="#DCFCE7"
        pillFg="#15803D"
        PillIcon={Check}
        pillLabel="You're in the clear"
      />
    )
  }
  return (
    <EmptyShell
      iconColor={AGENT_ORANGE}
      Icon={Rocket}
      title="No active applications"
      body="Swipe right on a job card and our agent will apply for you automatically."
      pillBg="rgba(232,112,42,0.12)"
      pillFg={AGENT_ORANGE}
      PillIcon={Rocket}
      pillLabel="Swipe right on any job"
    />
  )
}

function EmptyShell({ Icon, iconColor, title, body, pillBg, pillFg, PillIcon, pillLabel }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "48px 40px", textAlign: "center",
    }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon size={20} color={iconColor} />
        <span style={{ fontSize: 17, fontWeight: 700, color: INK, letterSpacing: "-0.3px" }}>{title}</span>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: INK_MUTED, lineHeight: 1.55, maxWidth: 320 }}>{body}</p>
      <div style={{
        marginTop: 24, display: "inline-flex", alignItems: "center", gap: 6,
        padding: "9px 14px", borderRadius: 20, background: pillBg, color: pillFg,
      }}>
        <PillIcon size={13} />
        <span style={{ fontSize: 12, fontWeight: 600 }}>{pillLabel}</span>
      </div>
    </div>
  )
}

function Shimmer() {
  return (
    <div style={{ padding: "0 20px", display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} style={{
          height: 92, background: SURFACE_ALT, borderRadius: 18,
          animation: "mapp-pulse 1.4s ease-in-out infinite",
        }} />
      ))}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
export default function MobileApplicationsTab({
  userId, user, resumeId,
  onTabChange, activeTab, pendingApplications,
}) {
  const [applications, setApplications] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [tab, setTab] = useState("review")
  const [progressApp, setProgressApp] = useState(null)
  const initialTabSet = useRef(false)

  useEffect(() => {
    if (!userId) return
    setLoaded(false)
    const unsub = onSnapshot(
      query(collection(db, "users", userId, "applications"), orderBy("appliedAt", "desc")),
      snap => {
        setApplications(snap.docs.map(normalize))
        setLoaded(true)
      },
      err => {
        console.error("Failed to watch applications:", err)
        setLoaded(true)
      }
    )
    return () => unsub()
  }, [userId])

  const groups = useMemo(() => {
    const out = { review: [], inProgress: [], submitted: [], failed: [] }
    for (const app of applications) out[bucketOf(app.status)].push(app)
    return out
  }, [applications])

  // Auto-select the first non-empty tab once data arrives
  useEffect(() => {
    if (!loaded || initialTabSet.current) return
    initialTabSet.current = true
    if (groups.review.length > 0)        setTab("review")
    else if (groups.inProgress.length)   setTab("inProgress")
    else if (groups.submitted.length)    setTab("submitted")
    else if (groups.failed.length)       setTab("failed")
  }, [loaded, groups])

  const visible = groups[tab] || []

  const deleteApplication = async (id) => {
    if (!userId || !id) return
    try { await deleteDoc(doc(db, "users", userId, "applications", id)) }
    catch (err) {
      console.error("Failed to delete application:", err)
      toast.error("Could not delete application")
    }
  }

  const retryApplication = async (app) => {
    if (!userId || !app.jobUrl) return
    try {
      const res = await fetch(APPLICATIONS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId, jobUrl: app.jobUrl, jobTitle: app.jobTitle,
          company: app.company, logoUrl: app.logoUrl || "",
          description: app.description || "", fcmToken: "",
        }),
      })
      if (res.status !== 201) throw new Error(`Application API error ${res.status}`)
      await deleteApplication(app.id)
      toast.success(`Agent Apply restarted for ${app.company}`)
      setTab("inProgress")
    } catch (err) {
      console.error("Failed to retry application:", err)
      toast.error("Could not restart application")
    }
  }

  const reviewCount = groups.review.length

  // Progress sheet takes over the whole surface when an app card is tapped.
  if (progressApp) {
    return (
      <MobileApplicationProgressSheet
        application={progressApp}
        userId={userId}
        resumeId={resumeId}
        user={user}
        onClose={() => setProgressApp(null)}
      />
    )
  }

  const total = applications.length
  const subtitle = total === 0
    ? "Applications your agent has queued or sent"
    : reviewCount > 0
      ? `${total} application${total === 1 ? "" : "s"} · ${reviewCount} need${reviewCount === 1 ? "s" : ""} review`
      : `${total} application${total === 1 ? "" : "s"} tracked`

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 150,
      background: BG, color: INK, fontFamily: FF,
      display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @keyframes mapp-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* Scroll area */}
      <div style={{
        flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
        paddingTop: "calc(14px + env(safe-area-inset-top))",
        paddingBottom: 108,
      }}>
        {/* Header */}
        <div style={{ padding: "0 20px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{
              margin: 0, fontSize: 30, fontWeight: 800, color: INK,
              letterSpacing: "-0.8px", lineHeight: 1.1,
            }}>Applications</h1>
            {reviewCount > 0 && (
              <span style={{
                padding: "3px 9px", borderRadius: 999,
                background: AGENT_ORANGE, color: "#fff",
                fontSize: 11, fontWeight: 700,
                boxShadow: "0 2px 10px rgba(232,112,42,0.35)",
              }}>{reviewCount}</span>
            )}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: INK_MUTED, fontWeight: 500 }}>
            {subtitle}
          </div>
        </div>

        {/* Chip row */}
        <div style={{ padding: "0 20px 12px" }}>
          <div style={{
            padding: 5, background: SURFACE_ALT, borderRadius: 24,
            display: "flex", gap: 4, overflowX: "auto", scrollbarWidth: "none",
          }}>
            {TABS.map(t => (
              <TabChip
                key={t.id}
                label={t.label}
                count={groups[t.id].length}
                selected={tab === t.id}
                destructive={t.destructive}
                onClick={() => setTab(t.id)}
              />
            ))}
          </div>
        </div>

        {/* List */}
        {!loaded ? (
          <Shimmer />
        ) : visible.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
            {visible.map(app => (
              <AppCard
                key={app.id}
                app={app}
                onTap={setProgressApp}
                onDelete={deleteApplication}
                onRetry={retryApplication}
              />
            ))}
          </div>
        )}
      </div>

      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        pendingApplications={pendingApplications || 0}
      />
    </div>
  )
}
